// Backend enums and constants for frontend use
export const ALLOWED_CATEGORIES = [
  "New",
  "Bill", 
  "Cancel",
  "Transfer",
  "Disconnect",
  "Bill + Cancel",
  "Bill + Restore"
] as const;

export const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming"
] as const;

export const US_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
] as const;

export const USER_ROLES = [
  "agent",
  "sales_team_lead", 
  "processor_agent",
  "processor_team_lead",
  "floor_manager",
  "director",
  "admin"
] as const;

export const SALE_STATUSES = [
  "PENDING",
  "APPROVED", 
  "DECLINED",
  "ERROR"
] as const;

export const PROCESSING_STATUSES = [
  "PENDING_ASSIGNMENT",
  "ASSIGNED",
  "IN_PROCESS", 
  "COMPLETED",
  "CANCELLED",
  "CHARGEBACK",
  "DOCUMENTS_REQUIRED"
] as const;

export type AllowedCategory = typeof ALLOWED_CATEGORIES[number];
export type USState = typeof US_STATES[number];
export type UserRole = typeof USER_ROLES[number];
export type SaleStatus = typeof SALE_STATUSES[number];
export type ProcessingStatus = typeof PROCESSING_STATUSES[number];

// Role hierarchy for approvals
export const ROLE_HIERARCHY = {
  'admin': ['admin', 'director', 'floor_manager', 'sales_team_lead', 'processor_team_lead', 'agent', 'processor_agent'],
  'director': ['director', 'floor_manager', 'sales_team_lead', 'processor_team_lead', 'agent', 'processor_agent'],
  'floor_manager': ['floor_manager', 'sales_team_lead', 'processor_team_lead', 'agent', 'processor_agent'],
  'sales_team_lead': ['sales_team_lead', 'agent'],
  'processor_team_lead': ['processor_team_lead', 'processor_agent'],
  'agent': [],
  'processor_agent': []
} as const;

// Role permissions for various actions  
export const ROLE_PERMISSIONS = {
  canCreateSales: ['agent', 'sales_team_lead', 'floor_manager', 'director', 'admin'] as string[],
  canApproveSales: ['sales_team_lead', 'floor_manager', 'director', 'admin'] as string[],
  canProcessSales: ['processor_agent', 'processor_team_lead', 'floor_manager', 'director', 'admin'] as string[],
  canManageUsers: ['director', 'admin'] as string[],
  canViewAllTransactions: ['floor_manager', 'director', 'admin'] as string[],
  canRefund: ['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin'] as string[],
  canVoid: ['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin'] as string[],
  canDecryptCardData: ['director', 'admin'] as string[],
  canDecryptSSNOnly: ['processor_agent', 'processor_team_lead', 'floor_manager', 'director', 'admin'] as string[],
  canManageSettings: ['admin', 'director', 'floor_manager', 'sales_team_lead'] as string[]
};

// Tabs hidden from these roles' navigation (sidebar + dashboard quick actions).
// The pages themselves are unchanged.
const HIDDEN_TABS_BY_ROLE: Record<string, string[]> = {
  floor_manager: [
    '/my-pending-processing', '/my-processed', '/my-chargebacks', '/settings',
    '/processing', '/pending-processing', '/approvals', '/analytics',
  ],
  processor_team_lead: [
    '/my-pending-processing', '/my-processed', '/my-chargebacks', '/settings',
    '/processing', '/pending-processing', '/approvals', '/analytics',
  ],
};

export const isTabHidden = (role: string | undefined, path: string): boolean =>
  !!role && (HIDDEN_TABS_BY_ROLE[role] || []).includes(path);